import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, X, Pencil, Loader2 } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface LeadScheduledMessagesProps {
  leadId: string;
}

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Pendente", variant: "default" },
  processing: { label: "Processando", variant: "secondary" },
  sent: { label: "Enviada", variant: "outline" },
  cancelled: { label: "Cancelada", variant: "secondary" },
  failed: { label: "Falhou", variant: "destructive" },
};

const CANCEL_REASON_LABELS: Record<string, string> = {
  user_cancelled: "Cancelado pelo usuário",
  conversation_reopened: "Conversa reaberta",
  conversation_no_longer_closed: "Conversa não está mais fechada",
  channel_not_whatsapp: "Canal não é WhatsApp",
  no_active_whatsapp_integration: "Sem integração WhatsApp ativa",
  conversation_not_found: "Conversa não encontrada",
};

export function LeadScheduledMessages({ leadId }: LeadScheduledMessagesProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["scheduled-messages", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scheduled_messages")
        .select("id, body, scheduled_at, status, cancel_reason, sent_at, created_at, source_kind")
        .eq("lead_id", leadId)
        .order("scheduled_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("cancel_scheduled_message", { _id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-messages", leadId] });
      toast({ title: "✅ Agendamento cancelado" });
    },
    onError: (err: any) => {
      toast({ title: "❌ Erro ao cancelar", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body, scheduledAt }: { id: string; body: string; scheduledAt: string }) => {
      const { data, error } = await supabase.rpc("update_scheduled_message", {
        _id: id,
        _body: body,
        _scheduled_at: scheduledAt,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-messages", leadId] });
      setEditingId(null);
      toast({ title: "✅ Agendamento atualizado" });
    },
    onError: (err: any) => {
      toast({ title: "❌ Erro ao atualizar", description: err.message, variant: "destructive" });
    },
  });

  const startEdit = (msg: any) => {
    setEditingId(msg.id);
    setEditBody(msg.body);
    const d = new Date(msg.scheduled_at);
    setEditDate(format(d, "yyyy-MM-dd"));
    setEditTime(format(d, "HH:mm"));
  };

  const submitEdit = () => {
    if (!editingId || !editBody.trim() || !editDate || !editTime) return;
    const scheduledAt = new Date(`${editDate}T${editTime}:00`).toISOString();
    updateMutation.mutate({ id: editingId, body: editBody.trim(), scheduledAt });
  };

  if (isLoading || messages.length === 0) return null;

  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:text-foreground transition-colors group">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          <span>Mensagens Agendadas</span>
          <Badge variant="secondary" className="text-xs h-5 px-1.5">
            {messages.filter((m) => m.status === "pending").length}
          </Badge>
        </div>
        <ChevronDown className="w-4 h-4 text-muted-foreground group-data-[state=open]:rotate-180 transition-transform" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pt-1">
        {messages.map((msg) => {
          const config = STATUS_CONFIG[msg.status] || STATUS_CONFIG.pending;
          const isEditing = editingId === msg.id;
          const isPending = msg.status === "pending";

          return (
            <div key={msg.id} className="rounded-md border p-3 space-y-2 text-sm">
              {/* Header: status + date */}
              <div className="flex items-center justify-between">
                <Badge variant={config.variant} className="text-xs">
                  {config.label}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(msg.scheduled_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>

              {/* Body */}
              {isEditing ? (
                <div className="space-y-2">
                  <Textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    className="text-sm"
                  />
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                      className="w-36 text-sm"
                    />
                    <Input
                      type="time"
                      value={editTime}
                      onChange={(e) => setEditTime(e.target.value)}
                      className="w-28 text-sm"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} disabled={updateMutation.isPending}>
                      Cancelar
                    </Button>
                    <Button size="sm" onClick={submitEdit} disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      Salvar
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground line-clamp-3">{msg.body}</p>
              )}

              {/* Cancel reason */}
              {msg.status === "cancelled" && msg.cancel_reason && (
                <p className="text-xs text-muted-foreground italic">
                  Motivo: {CANCEL_REASON_LABELS[msg.cancel_reason] || msg.cancel_reason}
                </p>
              )}

              {/* Sent timestamp */}
              {msg.status === "sent" && msg.sent_at && (
                <p className="text-xs text-muted-foreground">
                  Enviada em {format(new Date(msg.sent_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              )}

              {/* Actions (only pending) */}
              {isPending && !isEditing && (
                <div className="flex gap-2 pt-1">
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => startEdit(msg)}>
                    <Pencil className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    onClick={() => cancelMutation.mutate(msg.id)}
                    disabled={cancelMutation.isPending}
                  >
                    <X className="w-3 h-3 mr-1" /> Cancelar
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}
